import {
    ChangeEvent,
    DragEvent,
    FC,
    ReactElement,
    useState,
} from 'react'
import classNames from 'classnames'

import { ErrorLabel, Icon } from '../../../../../components'
import { IconButton } from '../../../../../compositions'
import { ValkTalkPostImage } from '../../../../../entities/ValkTalkPost/ValkTalkPost'
import trans from '../../../../../helpers/trans'
import { VALK_TALK_POST_IMAGE_MIME_TYPES } from '../../validations/valkTalkPostFormValidation'

import './ValkTalkPostImagesEditor.scss'

interface ValkTalkPostImagesEditorProps {
    images: ValkTalkPostImage[]
    disabled?: boolean
    errors?: string[]
    onAdd: (files: File[]) => void
    onMove: (fromIndex: number, toIndex: number) => void
    onRemove: (imageId: string) => void
    className?: string
}

export const ValkTalkPostImagesEditor: FC<ValkTalkPostImagesEditorProps> = ({
    images,
    disabled,
    errors = [],
    onAdd,
    onMove,
    onRemove,
    className = '',
}): ReactElement => {
    const [dragIndex, setDragIndex] = useState<number>()

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const files = Array.from(event.target.files || [])

        // Reset so selecting the same file again still triggers a change
        event.target.value = ''

        if (files.length) {
            onAdd(files)
        }
    }

    const handleDragStart = (event: DragEvent<HTMLLIElement>, index: number): void => {
        // Firefox doesn't start dragging without data
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', images[index].id)

        setDragIndex(index)
    }

    const handleDragOver = (event: DragEvent<HTMLLIElement>, index: number): void => {
        if (dragIndex === undefined) {
            return
        }

        event.preventDefault()

        if (dragIndex !== index) {
            onMove(dragIndex, index)
            setDragIndex(index)
        }
    }

    return (
        <div className={`valk-talk-post-images-editor ${className}`}>
            <div className="valk-talk-post-images-editor__header">
                <h2 className="valk-talk-post-images-editor__title">
                    {trans('containers.forms.valkTalkPostForm.images')}
                </h2>
                {images.length > 1 && (
                    <p className="valk-talk-post-images-editor__hint">
                        {trans('containers.forms.valkTalkPostForm.imagesOrderHint')}
                    </p>
                )}
            </div>

            <ul className="valk-talk-post-images-editor__list">
                {images.map((image, index) => (
                    <li
                        key={image.id}
                        draggable={! disabled}
                        onDragStart={event => handleDragStart(event, index)}
                        onDragOver={event => handleDragOver(event, index)}
                        onDrop={event => event.preventDefault()}
                        onDragEnd={() => setDragIndex(undefined)}
                        className={classNames('valk-talk-post-images-editor__item', {
                            'valk-talk-post-images-editor__item--is-dragging': index === dragIndex,
                        })}
                    >
                        <img
                            src={image.url}
                            alt={trans('containers.forms.valkTalkPostForm.imageAlt', { number: String(index + 1) })}
                            className="valk-talk-post-images-editor__image"
                            draggable={false}
                        />

                        {index === 0 && (
                            <span className="valk-talk-post-images-editor__cover-badge">
                                {trans('containers.forms.valkTalkPostForm.coverImage')}
                            </span>
                        )}

                        <Icon name="draggable" className="valk-talk-post-images-editor__drag-icon" />

                        <div className="valk-talk-post-images-editor__actions">
                            <IconButton
                                hideLabel
                                icon="chevron-left"
                                text={trans('containers.forms.valkTalkPostForm.moveImageLeft')}
                                disabled={disabled || index === 0}
                                onClick={() => onMove(index, index - 1)}
                                className="valk-talk-post-images-editor__action"
                                iconClassName="valk-talk-post-images-editor__action-icon"
                            />
                            <IconButton
                                hideLabel
                                icon="trash"
                                text={trans('containers.forms.valkTalkPostForm.removeImage')}
                                disabled={disabled}
                                onClick={() => onRemove(image.id)}
                                className="valk-talk-post-images-editor__action"
                                iconClassName="valk-talk-post-images-editor__action-icon"
                            />
                            <IconButton
                                hideLabel
                                icon="chevron-right"
                                text={trans('containers.forms.valkTalkPostForm.moveImageRight')}
                                disabled={disabled || index === images.length - 1}
                                onClick={() => onMove(index, index + 1)}
                                className="valk-talk-post-images-editor__action"
                                iconClassName="valk-talk-post-images-editor__action-icon"
                            />
                        </div>
                    </li>
                ))}

                <li className="valk-talk-post-images-editor__add-item">
                    <label
                        className={classNames('valk-talk-post-images-editor__add-button', {
                            'valk-talk-post-images-editor__add-button--is-disabled': disabled,
                        })}
                    >
                        <input
                            multiple
                            type="file"
                            accept={VALK_TALK_POST_IMAGE_MIME_TYPES.join(',')}
                            disabled={disabled}
                            onChange={handleFileChange}
                            className="valk-talk-post-images-editor__file-input"
                        />
                        <Icon name="plus" className="valk-talk-post-images-editor__add-icon" />
                        {trans('containers.forms.valkTalkPostForm.addImages')}
                    </label>
                </li>
            </ul>

            {errors.map(error => (
                <ErrorLabel key={error} text={error} className="valk-talk-post-images-editor__error" />
            ))}
        </div>
    )
}
